import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import {
    DndContext, 
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-hot-toast';

import {
    Settings as SettingsIcon,
    Users,
    List,
    Shield,
    Key,
    Bot,
    MessageSquare,
    Save,
    Plus,
    Trash2,
    UserPlus,
    CheckCircle2,
    Eye,
    EyeOff,
    Edit2,
    Briefcase,
    X,
    Wallet,
    Upload,
    Link2,
    User as UserIcon,
    ShieldCheck,
    CreditCard,
    LayoutDashboard,
    CheckSquare,
    TrendingUp,
    TrendingDown,
    Inbox,
    AlertCircle,
    Cpu,
    ListTodo,
    ChevronUp,
    ChevronDown,
    GripVertical
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';

const SortableProvider = ({ provider, onEdit, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: provider.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 50 : 0,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div 
            ref={setNodeRef} 
            style={style}
            className={`flex items-center justify-between p-4 bg-gray-800/50 border border-gray-700 rounded-xl mb-3 hover:border-blue-500/50 transition-all group ${isDragging ? 'shadow-2xl border-blue-500 shadow-blue-500/20' : ''}`}
        >
            <div className="flex items-center gap-4">
                <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-300 p-1">
                    <GripVertical size={20} />
                </div>
                <div>
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{provider.name}</span>
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-blue-900/20 text-blue-400 border border-blue-500/20 rounded-full">{provider.provider_type}</span>
                        {!provider.is_active && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-red-900/20 text-red-400 border border-red-500/20 rounded-full">Выключен</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{provider.model_name || 'По умолчанию'} • {provider.api_base ? 'Custom URL' : 'Standard API'}</div>
                </div>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => onEdit(provider)} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors">
                    <Edit2 size={16} />
                </button>
                <button 
                    onClick={(e) => { e.stopPropagation(); if(window.confirm('Удалить провайдера?')) onDelete(provider.id); }} 
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
                >
                    <Trash2 size={16} />
                </button>
            </div>
        </div>
    );
};

const Settings = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const isAdmin = ['admin', 'superadmin'].includes(user?.role);
    const [activeTab, setActiveTab] = (isAdmin ? useState('general') : useState('profile'));
    const [showKey, setShowKey] = useState(false);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [userForm, setUserForm] = useState({
        can_access_site: false, can_view_company_balance: false, can_view_month_balance: false, can_view_finance: false
    });

    // Wallet State
    const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
    const [editingWallet, setEditingWallet] = useState(null);
    const [walletForm, setWalletForm] = useState({ name: '', balance: '', owner_id: '' });

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Data Fetching
    const { data: settings } = useQuery({
        queryKey: ['settings'],
        queryFn: async () => (await client.get('/settings')).data
    });

    const { data: categories } = useQuery({
        queryKey: ['categories'],
        queryFn: async () => (await client.get('/categories')).data
    });

    const { data: appUsers } = useQuery({
        queryKey: ['users'],
        queryFn: async () => (await client.get('/users')).data
    });

    const { data: wallets } = useQuery({
        queryKey: ['wallets-list'],
        queryFn: async () => (await client.get('/wallets')).data
    });



    const { data: botStatus } = useQuery({
        queryKey: ['bot-status'],
        queryFn: async () => (await client.get('/settings/bot-status')).data,
        refetchInterval: 5000 // Poll every 5s
    });

    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // AI Queries
    const { data: aiProviders, refetch: refetchAIProviders } = useQuery({
        queryKey: ['ai-providers'],
        queryFn: async () => (await client.get('/ai/providers')).data,
        enabled: isAdmin && activeTab === 'ai'
    });

    const { data: aiPrompts } = useQuery({
        queryKey: ['ai-prompts'],
        queryFn: async () => (await client.get('/ai/prompts')).data,
        enabled: isAdmin && activeTab === 'ai'
    });

    const [isAIModalOpen, setIsAIModalOpen] = useState(false);
    const [editingAI, setEditingAI] = useState(null);
    const [aiForm, setAiForm] = useState({
        name: '', provider_type: 'openai', api_key: '', api_base: '', model_name: '', is_active: true
    });

    const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState(null);
    const [promptForm, setPromptForm] = useState({
        slug: '', name: '', template: '', description: ''
    });

    // AI Mutations
    const configMutation = useMutation({
        mutationFn: (data) => client.post('/ai/providers', data),
        onSuccess: () => {
            queryClient.invalidateQueries(['ai-providers']);
            setIsAIModalOpen(false);
            setEditingAI(null);
            toast.success('Настройки сохранены');
        }
    });

    const reorderAIProvidersMutation = useMutation({
        mutationFn: (ids) => client.post('/ai/providers/reorder', ids),
        onSuccess: () => queryClient.invalidateQueries(['ai-providers'])
    });

    const deleteAIProviderMutation = useMutation({
        mutationFn: (id) => client.delete(`/ai/providers/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries(['ai-providers']);
            toast.success('Провайдер удален');
        }
    });

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = aiProviders.findIndex(p => p.id === active.id);
            const newIndex = aiProviders.findIndex(p => p.id === over.id);
            const newProviders = arrayMove(aiProviders, oldIndex, newIndex);
            reorderAIProvidersMutation.mutate(newProviders.map(p => p.id));
        }
    };


    // Mutations
    const updateSettingsMutation = useMutation({
        mutationFn: (data) => client.post('/settings', data),
        onSuccess: () => {
            queryClient.invalidateQueries(['settings']);
            setErrorMsg('');
        },
        onError: (err) => setErrorMsg('Ошибка сохранения: ' + (err.response?.data?.detail || err.message))
    });

    const addCategoryMutation = useMutation({
        mutationFn: (data) => client.post('/categories', data),
        onSuccess: () => {
            queryClient.invalidateQueries(['categories']);
            setErrorMsg('');
        },
        onError: (err) => setErrorMsg('Ошибка добавления: ' + (err.response?.data?.detail || err.message))
    });

    const deleteCategoryMutation = useMutation({
        mutationFn: (id) => client.delete(`/categories/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries(['categories']);
            setErrorMsg('');
        },
        onError: (err) => setErrorMsg('Ошибка удаления: ' + (err.response?.data?.detail || err.message))
    });

    const createUserMutation = useMutation({
        mutationFn: (data) => client.post('/users', data),
        onSuccess: () => {
            queryClient.invalidateQueries(['users']);
            setIsUserModalOpen(false);
            setErrorMsg('');
        },
        onError: (err) => setErrorMsg('Ошибка создания пользователя: ' + (err.response?.data?.detail || err.message))
    });

    const updateUserMutation = useMutation({
        mutationFn: ({ id, data }) => client.patch(`/users/${id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['users']);
            setIsUserModalOpen(false);
            setErrorMsg('');
        },
        onError: (err) => setErrorMsg('Ошибка обновления пользователя: ' + (err.response?.data?.detail || err.message))
    });

    const deleteUserMutation = useMutation({
        mutationFn: (id) => client.delete(`/users/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries(['users']);
            setErrorMsg('');
        },
        onError: (err) => setErrorMsg('Ошибка удаления пользователя: ' + (err.response?.data?.detail || err.message))
    });

    const toggle2FAMutation = useMutation({
        mutationFn: (enable) => client.post('/users/2fa/toggle', { enable }),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['users']);
            // Also need to update current user context or simply reload
            setSuccessMsg(data.is_2fa_enabled ? 'Двухфакторная аутентификация включена' : 'Двухфакторная аутентификация отключена');
            setTimeout(() => setSuccessMsg(''), 5000);
            setTimeout(() => window.location.reload(), 1500); // Reload to update auth context
        },
        onError: (err) => {
            const errorData = err.response?.data?.detail || err.message;
            setErrorMsg('Ошибка 2FA: ' + (typeof errorData === 'object' ? JSON.stringify(errorData) : errorData));
        }
    });

    const updateProfileMutation = useMutation({
        mutationFn: (data) => client.patch('/users/profile', data),
        onSuccess: () => {
            queryClient.invalidateQueries(['users']);
            setSuccessMsg('Профиль обновлен');
            setTimeout(() => setSuccessMsg(''), 3000);
            setTimeout(() => window.location.reload(), 1000);
        },
        onError: (err) => {
            const errorData = err.response?.data?.detail || err.message;
            setErrorMsg('Ошибка: ' + (typeof errorData === 'object' ? JSON.stringify(errorData) : errorData));
        }
    });

    const uploadProfilePhotoMutation = useMutation({
        mutationFn: (formData) => client.post('/users/profile/photo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        onSuccess: () => {
            queryClient.invalidateQueries(['users']);
            setSuccessMsg('Фото обновлено');
            setTimeout(() => setSuccessMsg(''), 3000);
            setTimeout(() => window.location.reload(), 1000);
        },
        onError: (err) => {
            const errorData = err.response?.data?.detail || err.message;
            setErrorMsg('Ошибка загрузки: ' + (typeof errorData === 'object' ? JSON.stringify(errorData) : errorData));
        }
    });

    // Wallet Mutations
    const createWalletMutation = useMutation({
        mutationFn: (data) => client.post('/wallets', data),
        onSuccess: () => {
            queryClient.invalidateQueries(['wallets-list']); // update this list
            queryClient.invalidateQueries(['dashboard']); // and dashboard
            setIsWalletModalOpen(false);
            setErrorMsg('');
        },
        onError: (err) => setErrorMsg('Ошибка: ' + (err.response?.data?.detail || err.message))
    });

    const updateWalletMutation = useMutation({
        mutationFn: ({ id, data }) => client.patch(`/wallets/${id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['wallets-list']);
            queryClient.invalidateQueries(['dashboard']);
            setIsWalletModalOpen(false);
            setErrorMsg('');
        },
        onError: (err) => setErrorMsg('Ошибка: ' + (err.response?.data?.detail || err.message))
    });

    const deleteWalletMutation = useMutation({
        mutationFn: (id) => client.delete(`/wallets/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries(['wallets-list']);
            queryClient.invalidateQueries(['dashboard']);
            setErrorMsg('');
        },
        onError: (err) => setErrorMsg('Ошибка: ' + (err.response?.data?.detail || err.message))
    });

    const [newCat, setNewCat] = useState({ name: '', type: 'expense' });
    const [cfg, setCfg] = useState({});
    const [bots, setBots] = useState([]);
    const [chats, setChats] = useState([]);
    const [selectedBotId, setSelectedBotId] = useState('');

    // Sync cfg with settings data when loaded
    React.useEffect(() => {
        if (settings) {
            setCfg(settings);
            if (settings.ticket_notify_bot_id) {
                setSelectedBotId(settings.ticket_notify_bot_id);
            }
        }
    }, [settings]);

    // Fetch bots when activeTab === 'bot'
    React.useEffect(() => {
        if (activeTab === 'bot') {
            client.get('/bots').then(res => {
                setBots(res.data);
            }).catch(err => {
                console.error("Failed to fetch bots:", err);
            });
        }
    }, [activeTab]);

    // Fetch chats when selectedBotId changes
    React.useEffect(() => {
        if (selectedBotId) {
            client.get(`/bots/${selectedBotId}/chats`).then(res => {
                setChats(res.data);
            }).catch(err => {
                console.error("Failed to fetch chats:", err);
                setChats([]);
            });
        } else {
            setChats([]);
        }
    }, [selectedBotId]);

    const handleBotChange = (botId) => {
        setSelectedBotId(botId);
        setCfg(prev => ({ ...prev, ticket_notify_bot_id: botId, ticket_notify_chat_id: '' }));
    };

    const handleChatChange = (chatId) => {
        setCfg(prev => ({ ...prev, ticket_notify_chat_id: chatId }));
    };



    const handleSaveCfg = async () => {
        // Clear previous messages
        setErrorMsg('');
        setSuccessMsg('');

        try {
            await client.post('/settings', cfg);

            // Show generic success message
            setSuccessMsg('Настройки сохранены');
            setTimeout(() => setSuccessMsg(''), 3000);

            queryClient.invalidateQueries(['settings']);
        } catch (err) {
            setErrorMsg('Ошибка сохранения: ' + (err.response?.data?.detail || err.message));
            setTimeout(() => setErrorMsg(''), 5000);
        }
    };

    const uploadLogoMutation = useMutation({
        mutationFn: (formData) => client.post('/settings/logo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        onSuccess: (data) => {
            setCfg(prev => ({ ...prev, company_logo: data.url }));
            setErrorMsg('');
        },
        onError: (err) => setErrorMsg('Ошибка загрузки: ' + (err.response?.data?.detail || err.message))
    });

    const handleUploadLogo = (file) => {
        const formData = new FormData();
        formData.append('file', file);
        uploadLogoMutation.mutate(formData);
    };

    const uploadFaviconMutation = useMutation({
        mutationFn: (formData) => client.post('/settings/favicon', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        onSuccess: (data) => {
            setCfg(prev => ({ ...prev, company_favicon: data.url }));
            setErrorMsg('');
        },
        onError: (err) => setErrorMsg('Ошибка загрузки favicon: ' + (err.response?.data?.detail || err.message))
    });

    const handleUploadFavicon = (file) => {
        const formData = new FormData();
        formData.append('file', file);
        uploadFaviconMutation.mutate(formData);
    };



    const tabs = [
        { id: 'profile', label: 'Мой профиль', icon: UserIcon },
        { id: 'dashboard', label: 'Дашборд', icon: LayoutDashboard },
        { id: 'general', label: 'Система', icon: SettingsIcon, roles: ['admin', 'superadmin'] },
        { id: 'tasks', label: 'Задачи', icon: CheckSquare, roles: ['admin', 'superadmin'] },
        { id: 'wallets', label: 'Кошельки', icon: Wallet, roles: ['admin', 'superadmin'] },
        { id: 'connections', label: 'Подключения', icon: Key, roles: ['admin', 'superadmin'] },
        { id: 'bot', label: 'Бот & Уведомления', icon: Bot, roles: ['admin', 'superadmin'] },

        { id: 'users', label: 'Пользователи', icon: Users, roles: ['admin', 'superadmin'] },
        { id: 'categories', label: 'Категории', icon: List, roles: ['admin', 'superadmin'] },
        { id: 'ai', label: 'Интеллект (AI)', icon: Cpu, roles: ['admin', 'superadmin'] },
    ].filter(tab => !tab.roles || tab.roles.includes(user?.role));

    const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null });

    return (
        <div className="flex flex-col md:flex-row gap-6 lg:gap-12 relative pb-20">
            {/* Sidebar Navigation */}
            <div className="w-full md:w-56 lg:w-72 shrink-0 flex md:flex-col overflow-x-auto md:overflow-visible gap-2 md:gap-1 pb-4 md:pb-0">
                <h1 className="text-2xl font-black text-white mb-4 md:mb-8 tracking-tighter px-2 hidden md:block">НАСТРОЙКИ</h1>
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-bold uppercase text-[10px] tracking-[0.2em] group whitespace-nowrap ${activeTab === tab.id
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                            : 'bg-neutral-900 md:bg-transparent text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300'
                            }`}
                    >
                        <tab.icon className="w-4 h-4" />
                        <span>{tab.label}</span>
                        {tab.id === 'bot' && botStatus && (
                            <div className={`w-2 h-2 rounded-full ml-auto ${botStatus.status === 'running' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' :
                                botStatus.status === 'starting' ? 'bg-amber-500 animate-pulse' :
                                    'bg-rose-500'
                                }`} />
                        )}
                    </button>
                ))}
            </div>


            {/* Content Area */}
            <div className="flex-1 min-w-0">
                {successMsg && (
                    <div className="mb-4 bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 p-4 rounded-2xl flex justify-between items-center animate-in fade-in slide-in-from-top-2">
                        <p className="text-xs font-bold">{successMsg}</p>
                        <button onClick={() => setSuccessMsg('')}><X className="w-4 h-4" /></button>
                    </div>
                )}
                {errorMsg && (
                    <div className="mb-4 bg-rose-500/10 border border-rose-500/20 text-rose-500 p-4 rounded-2xl flex justify-between items-center animate-in fade-in slide-in-from-top-2">
                        <p className="text-xs font-bold">{errorMsg}</p>
                        <button onClick={() => setErrorMsg('')}><X className="w-4 h-4" /></button>
                    </div>
                )}
                <div className="bg-neutral-900 border border-neutral-800 rounded-[2.5rem] p-4 sm:p-6 lg:p-10 shadow-2xl min-h-[600px] max-w-6xl mx-auto">

                    {activeTab === 'profile' && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="border-b border-neutral-800 pb-8">
                                <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Мой профиль</h3>
                                <p className="text-neutral-500 text-sm font-medium">Персональные данные и безопасность аккаунта.</p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="lg:col-span-2 space-y-8">
                                    <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] flex flex-col md:flex-row items-center gap-8">
                                        <div className="relative group/photo shrink-0">
                                            {user?.photo_url ? (
                                                <img
                                                    src={user.photo_url.startsWith('/') ? (user.photo_url.startsWith('/api') ? user.photo_url : `/api${user.photo_url}`) : user.photo_url}
                                                    alt="Profile"
                                                    className="w-32 h-32 rounded-[2.5rem] border-4 border-blue-600/20 object-cover shadow-2xl"
                                                />
                                            ) : (
                                                <div className="w-32 h-32 bg-neutral-800 rounded-[2.5rem] flex items-center justify-center font-black text-4xl text-neutral-600 border-4 border-neutral-800 shadow-inner">
                                                    {user?.fio?.[0]?.toUpperCase() || user?.name?.[0]?.toUpperCase()}
                                                </div>
                                            )}
                                            <label className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-[2.5rem] opacity-0 group-hover/photo:opacity-100 transition-all cursor-pointer backdrop-blur-sm">
                                                <Upload className="w-8 h-8 text-white animate-bounce" />
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            const fd = new FormData();
                                                            fd.append('file', file);
                                                            uploadProfilePhotoMutation.mutate(fd);
                                                        }
                                                    }}
                                                />
                                            </label>
                                        </div>
                                        <div className="text-center md:text-left space-y-3">
                                            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                                                <span className={`text-[10px] font-black uppercase px-3 py-1 rounded-full ${user?.role === 'superadmin' ? 'bg-fuchsia-500/10 text-fuchsia-500' : user?.role === 'admin' ? 'bg-amber-500/10 text-amber-500' : 'bg-blue-500/10 text-blue-500'}`}>
                                                    {user?.role === 'superadmin' ? '⚡️ Супер-админ' : user?.role === 'admin' ? '👑 Администратор' : '👤 Сотрудник'}
                                                </span>
                                                {user?.telegram_id && (
                                                    <span className="text-[10px] font-bold text-blue-400 bg-blue-400/10 px-3 py-1 rounded-full border border-blue-400/20">
                                                        ID: {user?.telegram_id}
                                                    </span>
                                                )}
                                            </div>
                                            <h2 className="font-black text-white text-3xl tracking-tighter uppercase italic">{user?.fio || user?.name}</h2>
                                            <p className="text-neutral-500 text-xs font-bold uppercase tracking-widest">@{user?.name}</p>
                                        </div>
                                    </div>

                                    <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] space-y-6">
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Полное имя (ФИО)</label>
                                            <div className="flex flex-col sm:flex-row gap-3">
                                                <input
                                                    type="text"
                                                    defaultValue={user?.fio || ''}
                                                    placeholder="Иван Иванов"
                                                    className="flex-1 bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold"
                                                    id="profile_fio"
                                                />
                                                <button
                                                    onClick={() => {
                                                        const val = document.getElementById('profile_fio').value;
                                                        updateProfileMutation.mutate({ fio: val });
                                                    }}
                                                    className="bg-blue-600 hover:bg-blue-500 text-white p-4 rounded-2xl transition-all shadow-lg shadow-blue-600/20 active:scale-95 flex items-center justify-center shrink-0"
                                                >
                                                    <Save className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="pt-6 border-t border-neutral-800">
                                            <div className={`p-6 rounded-[2rem] border-2 transition-all flex items-center justify-between ${user?.is_2fa_enabled ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-neutral-900 border-neutral-800'}`}>
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${user?.is_2fa_enabled ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'bg-neutral-800 text-neutral-500'}`}>
                                                        <ShieldCheck className="w-6 h-6" />
                                                    </div>
                                                    <div>
                                                        <p className="font-black text-white text-sm uppercase tracking-tight">Защита аккаунта (2FA)</p>
                                                        <p className="text-[10px] text-neutral-500 mt-0.5 font-bold uppercase tracking-tighter">Вход только через код в Telegram</p>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => toggle2FAMutation.mutate(!user?.is_2fa_enabled)}
                                                    disabled={toggle2FAMutation.isLoading || (!user?.telegram_id && !user?.is_2fa_enabled)}
                                                    className={`w-12 h-6 rounded-full relative transition-colors ${user?.is_2fa_enabled ? 'bg-emerald-500' : 'bg-neutral-700'} ${(!user?.telegram_id && !user?.is_2fa_enabled) ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                                                >
                                                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${user?.is_2fa_enabled ? 'left-7' : 'left-1'}`} />
                                                </button>
                                            </div>
                                            {(!user?.telegram_id && !user?.is_2fa_enabled) && (
                                                <div className="mt-4 flex items-center gap-2 px-4 py-2 bg-amber-500/10 rounded-xl border border-amber-500/20">
                                                    <AlertCircle className="w-3 h-3 text-amber-500" />
                                                    <p className="text-[10px] text-amber-500 font-black uppercase tracking-widest">Привяжите Telegram ID для включения 2FA</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'dashboard' && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-neutral-800 pb-8">
                                <div>
                                    <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Настройки Дашборда</h3>
                                    <p className="text-neutral-500 text-sm font-medium">Персонализация главного экрана и отображение аналитики.</p>
                                </div>
                                <button onClick={handleSaveCfg} className="w-full md:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-600/20 active:scale-95">
                                    <Save className="w-4 h-4" />
                                    Сохранить настройки
                                </button>
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                                <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] space-y-6">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="p-2 bg-blue-600/10 rounded-xl">
                                            <List className="w-5 h-5 text-blue-500" />
                                        </div>
                                        <h4 className="text-sm font-black text-white uppercase tracking-widest">Период Аналитики</h4>
                                    </div>
                                    <p className="text-neutral-500 text-[10px] font-black uppercase tracking-widest">Выберите диапазон дат, который будет отображаться по умолчанию при входе.</p>
                                    <select
                                        value={cfg.dashboard_date_range || 'this_month'}
                                        onChange={(e) => setCfg({ ...cfg, dashboard_date_range: e.target.value })}
                                        className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold"
                                    >
                                        <option value="this_month">Этот месяц</option>
                                        <option value="last_month">Прошлый месяц</option>
                                        <option value="last_30_days">Последние 30 дней</option>
                                        <option value="this_year">Этот год</option>
                                        <option value="all_time">Все время</option>
                                    </select>
                                </div>

                                <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] space-y-6">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="p-2 bg-blue-600/10 rounded-xl">
                                            <LayoutDashboard className="w-5 h-5 text-blue-500" />
                                        </div>
                                        <h4 className="text-sm font-black text-white uppercase tracking-widest">Видимость Виджетов</h4>
                                    </div>
                                    <div className="grid gap-3">
                                        {[
                                            { id: 'balance', label: 'Карточка баланса' },
                                            { id: 'charts', label: 'Графики и аналитика' },
                                            { id: 'wallets', label: 'Список кошельков' },
                                            { id: 'tasks', label: 'Задачи и дедлайны' },
                                            { id: 'recent_transactions', label: 'Последние операции' },
                                            { id: 'annual_forecast', label: 'Годовой прогноз прибыли' },
                                            { id: 'operating_time', label: 'Срок работы системы' }
                                        ].map(widget => {
                                            const currentWidgets = cfg.dashboard_widgets ? cfg.dashboard_widgets.split(',') : ['balance', 'charts', 'wallets', 'tasks', 'recent_transactions'];
                                            const isChecked = currentWidgets.includes(widget.id);

                                            return (
                                                <button
                                                    key={widget.id}
                                                    onClick={() => {
                                                        const newWidgets = isChecked
                                                            ? currentWidgets.filter(id => id !== widget.id)
                                                            : [...currentWidgets, widget.id];
                                                        setCfg({ ...cfg, dashboard_widgets: newWidgets.join(',') });
                                                    }}
                                                    className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-all ${isChecked
                                                        ? 'bg-blue-600/10 border-blue-600 text-white shadow-inner'
                                                        : 'bg-neutral-900 border-neutral-800 text-neutral-500 opacity-60'
                                                        }`}
                                                >
                                                    <span className="text-[10px] font-black uppercase tracking-widest">{widget.label}</span>
                                                    {isChecked ? <div className="w-4 h-4 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" /> : null}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'general' && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-neutral-800 pb-8">
                                <div>
                                    <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Глобальные параметры</h3>
                                    <p className="text-neutral-500 text-sm font-medium">Основные настройки внешнего вида и валюты системы.</p>
                                </div>
                                <button onClick={handleSaveCfg} className="w-full md:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-600/20 active:scale-95">
                                    <Save className="w-4 h-4" />
                                    Сохранить изменения
                                </button>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="lg:col-span-2 space-y-8">
                                    <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-3">
                                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Название компании</label>
                                                <input
                                                    type="text"
                                                    value={cfg.company_name || ''}
                                                    onChange={(e) => setCfg({ ...cfg, company_name: e.target.value })}
                                                    className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold"
                                                    placeholder="MCClub"
                                                />
                                            </div>
                                            <div className="space-y-3">
                                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Символ валюты</label>
                                                <input
                                                    type="text"
                                                    value={cfg.currency_symbol || ''}
                                                    onChange={(e) => setCfg({ ...cfg, currency_symbol: e.target.value })}
                                                    className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-black text-xl"
                                                    placeholder="₽"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Логотип (URL или загрузка)</label>
                                            <div className="flex flex-col md:flex-row gap-3">
                                                <input
                                                    type="text"
                                                    value={cfg.company_logo || ''}
                                                    onChange={(e) => setCfg({ ...cfg, company_logo: e.target.value })}
                                                    className="flex-1 bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-mono text-xs"
                                                    placeholder="https://..."
                                                />
                                                <label className="bg-neutral-800 hover:bg-neutral-700 border-2 border-neutral-700 text-neutral-400 hover:text-white p-4 rounded-2xl cursor-pointer transition-all flex items-center justify-center gap-2 font-black uppercase text-[10px] tracking-widest">
                                                    <Upload className="w-4 h-4" />
                                                    Загрузить
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) handleUploadLogo(file);
                                                        }}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                </div>

                                <div className="space-y-8">
                                    <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] flex flex-col items-center">
                                        <h4 className="text-[10px] font-black text-neutral-500 uppercase tracking-widest mb-6 w-full text-center">Превью логотипа</h4>
                                        <div className="w-full aspect-square bg-neutral-900 rounded-[2rem] border-2 border-neutral-800 flex items-center justify-center p-8 overflow-hidden shadow-inner">
                                            {cfg.company_logo ? (
                                                <img
                                                    src={cfg.company_logo.startsWith('/') ? (cfg.company_logo.startsWith('/api') ? cfg.company_logo : `/api${cfg.company_logo}`) : cfg.company_logo}
                                                    alt="Preview"
                                                    className="max-w-full max-h-full object-contain"
                                                />
                                            ) : (
                                                <div className="text-neutral-700 text-4xl font-black italic uppercase">LOGO</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Favicon (.ico или картинка)</label>
                                        <div className="flex flex-col md:flex-row gap-3">
                                            <input
                                                type="text"
                                                value={cfg.company_favicon || ''}
                                                onChange={(e) => setCfg({ ...cfg, company_favicon: e.target.value })}
                                                className="flex-1 bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-mono text-xs"
                                                placeholder="https://..."
                                            />
                                            <label className="bg-neutral-800 hover:bg-neutral-700 border-2 border-neutral-700 text-neutral-400 hover:text-white p-4 rounded-2xl cursor-pointer transition-all flex items-center justify-center gap-2 font-black uppercase text-[10px] tracking-widest">
                                                <Upload className="w-4 h-4" />
                                                Загрузить
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    accept="image/*,.ico"
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) handleUploadFavicon(file);
                                                    }}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'connections' && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-neutral-800 pb-8">
                                <div>
                                    <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Внешние сервисы</h3>
                                    <p className="text-neutral-500 text-sm font-medium">Интеграции и API ключи для автоматизации процессов.</p>
                                </div>
                                <button onClick={handleSaveCfg} className="w-full md:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-600/20 active:scale-95">
                                    <Save className="w-4 h-4" />
                                    Сохранить ключи
                                </button>
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                                <div className="space-y-6 bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] relative overflow-hidden group">
                                    <div className="flex items-center gap-4 relative z-10">
                                        <div className="w-14 h-14 bg-sky-500/10 text-sky-500 rounded-2xl flex items-center justify-center shadow-inner">
                                            <Bot className="w-7 h-7" />
                                        </div>
                                        <div>
                                            <h4 className="font-black text-white uppercase text-sm tracking-widest">Telegram Bot</h4>
                                            <p className="text-[9px] text-neutral-500 font-bold uppercase tracking-widest mt-0.5">Уведомления и 2FA</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4 relative z-10">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Bot Token</label>
                                            <input
                                                type="password"
                                                value={cfg.bot_token || ''}
                                                onChange={(e) => setCfg({ ...cfg, bot_token: e.target.value })}
                                                className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-mono text-sm"
                                                placeholder="Token от @BotFather"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Main Chat ID</label>
                                            <input
                                                type="text"
                                                value={cfg.telegram_chat_id || ''}
                                                onChange={(e) => setCfg({ ...cfg, telegram_chat_id: e.target.value })}
                                                className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-mono text-sm"
                                                placeholder="-100..."
                                            />
                                        </div>
                                    </div>
                                    <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-sky-500 opacity-[0.03] blur-3xl rounded-full pointer-events-none" />
                                </div>
                            </div>
                        </div>
                    )}
                    {activeTab === 'tasks' && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-neutral-800 pb-8">
                                <div>
                                    <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Настройки Системы Задач</h3>
                                    <p className="text-neutral-500 text-sm font-medium">Выбор режима работы и параметров синхронизации.</p>
                                </div>
                                <button onClick={handleSaveCfg} className="w-full md:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-600/20 active:scale-95">
                                    <Save className="w-4 h-4" />
                                    Сохранить настройки
                                </button>
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                                <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] space-y-6">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="p-2 bg-blue-600/10 rounded-xl">
                                            <Shield className="w-5 h-5 text-blue-500" />
                                        </div>
                                        <h4 className="text-sm font-black text-white uppercase tracking-widest">Основной Режим</h4>
                                    </div>
                                    <p className="text-neutral-500 text-[10px] font-black uppercase tracking-widest mb-4">Выберите, где будут создаваться и храниться ваши задачи.</p>
                                    
                                    <div className="grid gap-3">
                                        {[
                                            { id: 'internal', label: 'Внутренняя система', desc: 'Задачи хранятся только на нашем сайте (максимальная скорость)' },
                                        ].map(mode => (
                                            <button
                                                key={mode.id}
                                                onClick={() => setCfg({ ...cfg, task_system_mode: mode.id })}
                                                className={`flex flex-col items-start p-5 rounded-2xl border-2 transition-all text-left ${cfg.task_system_mode !== 'hybrid' && cfg.task_system_mode !== 'yougile'
                                                    ? 'bg-blue-600/10 border-blue-600 text-white shadow-inner'
                                                    : 'bg-neutral-900 border-neutral-800 text-neutral-500 opacity-60 hover:opacity-100 hover:border-neutral-700'
                                                    }`}
                                            >
                                                <span className="text-[11px] font-black uppercase tracking-widest mb-1">{mode.label}</span>
                                                <span className="text-[9px] font-medium opacity-70 uppercase tracking-tighter">{mode.desc}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>


                            </div>
                        </div>
                    )}

                    {activeTab === 'wallets' && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-neutral-800 pb-8">
                                <div>
                                    <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Кошельки</h3>
                                    <p className="text-neutral-500 text-sm font-medium">Управление счетами и их владельцами.</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setEditingWallet(null);
                                        setWalletForm({ name: '', balance: '', owner_id: '' });
                                        setIsWalletModalOpen(true);
                                    }}
                                    className="w-full md:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-600/20 active:scale-95"
                                >
                                    <Plus className="w-4 h-4" />
                                    Добавить кошелек
                                </button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
                                {wallets?.map(wallet => {
                                    const owner = appUsers?.find(u => u.id === wallet.owner_id);
                                    return (
                                        <div key={wallet.id} className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] group hover:border-blue-500/50 transition-all duration-500 relative overflow-hidden">
                                            <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-all flex gap-2 z-20">
                                                <button
                                                    onClick={() => {
                                                        setEditingWallet(wallet);
                                                        setWalletForm({ name: wallet.name, balance: wallet.balance, owner_id: wallet.owner_id || '' });
                                                        setIsWalletModalOpen(true);
                                                    }}
                                                    className="w-10 h-10 bg-neutral-900/80 backdrop-blur-md rounded-xl flex items-center justify-center hover:bg-blue-600 text-neutral-400 hover:text-white transition-all border border-neutral-700 hover:border-blue-500"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => setConfirmModal({
                                                        isOpen: true,
                                                        message: `Удалить кошелек ${wallet.name}?`,
                                                        onConfirm: () => {
                                                            deleteWalletMutation.mutate(wallet.id);
                                                            setConfirmModal({ isOpen: false, message: '', onConfirm: null });
                                                        }
                                                    })}
                                                    className="w-10 h-10 bg-neutral-900/80 backdrop-blur-md rounded-xl flex items-center justify-center hover:bg-rose-600 text-neutral-400 hover:text-white transition-all border border-neutral-700 hover:border-rose-500"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>

                                            <div className="relative z-10">
                                                <div className="w-16 h-16 bg-blue-600/10 text-blue-500 rounded-2xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform duration-500 shadow-inner">
                                                    <CreditCard className="w-8 h-8" />
                                                </div>

                                                <h4 className="font-black text-white text-xl uppercase tracking-tight mb-1">{wallet.name}</h4>
                                                <p className="text-3xl font-black text-blue-500 mb-6 italic tracking-tighter">
                                                    {wallet.balance?.toLocaleString()} <span className="text-base not-italic text-neutral-600 ml-1">₽</span>
                                                </p>

                                                <div className="flex items-center gap-3">
                                                    <div className="w-6 h-6 rounded-full bg-neutral-800 flex items-center justify-center text-[10px] font-black text-neutral-500 uppercase">
                                                        {owner?.name?.[0] || '?'}
                                                    </div>
                                                    <span className="text-[10px] font-black text-neutral-500 uppercase tracking-widest">
                                                        {owner ? owner.name : 'Без владельца'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-blue-600 opacity-0 group-hover:opacity-[0.05] blur-[50px] rounded-full transition-opacity duration-500" />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {activeTab === 'bot' && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-neutral-800 pb-8">
                                <div>
                                    <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Telegram Бот</h3>
                                    <p className="text-neutral-500 text-sm font-medium">Конфигурация уведомлений и расписания.</p>
                                </div>
                                {botStatus && (
                                    <div className={`px-6 py-3 rounded-2xl border flex items-center gap-3 ${botStatus.status === 'running' ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-500 shadow-lg shadow-emerald-500/5' :
                                        botStatus.status === 'starting' ? 'bg-amber-500/5 border-amber-500/20 text-amber-500 shadow-lg shadow-amber-500/5' :
                                            'bg-rose-500/5 border-rose-500/20 text-rose-500 shadow-lg shadow-rose-500/5'
                                        }`}>
                                        <div className={`w-3 h-3 rounded-full ${botStatus.status === 'running' ? 'bg-emerald-500 animate-pulse' :
                                            botStatus.status === 'starting' ? 'bg-amber-500 animate-pulse' :
                                                'bg-rose-500'
                                            }`} />
                                        <span className="text-[10px] font-black uppercase tracking-widest">
                                            {botStatus.status === 'running' ? `Online (@${botStatus.username})` :
                                                botStatus.status === 'starting' ? 'Starting...' :
                                                    'Offline'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div className="space-y-8 lg:col-span-1">
                                    <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-3">
                                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Отчет (финансы)</label>
                                                <input
                                                    type="time"
                                                    value={cfg.bot_notification_time || '09:00'}
                                                    onChange={(e) => setCfg({ ...cfg, bot_notification_time: e.target.value })}
                                                    className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-mono text-center font-bold"
                                                />
                                            </div>
                                            <div className="space-y-3">
                                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Отчет (задачи)</label>
                                                <input
                                                    type="time"
                                                    value={cfg.bot_task_notification_time || '10:00'}
                                                    onChange={(e) => setCfg({ ...cfg, bot_task_notification_time: e.target.value })}
                                                    className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-mono text-center font-bold"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">URL Мини-приложения</label>
                                            <input
                                                type="text"
                                                value={cfg.frontend_url || ''}
                                                onChange={(e) => setCfg({ ...cfg, frontend_url: e.target.value })}
                                                className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-mono text-sm"
                                                placeholder="https://erp.millionconnections.club"
                                            />
                                        </div>
                                    </div>

                                    <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] space-y-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-blue-600/10 rounded-xl">
                                                    <Shield className="w-5 h-5 text-blue-500" />
                                                </div>
                                                <h4 className="text-sm font-black text-white uppercase tracking-widest">Telegram Proxy (Cloudflare)</h4>
                                            </div>
                                            <button
                                                onClick={() => setCfg({ ...cfg, use_telegram_proxy: cfg.use_telegram_proxy === 'false' ? 'true' : 'false' })}
                                                className={`w-12 h-6 rounded-full relative transition-colors ${cfg.use_telegram_proxy !== 'false' ? 'bg-blue-600 shadow-[0_0_10px_rgba(37,99,235,0.4)]' : 'bg-neutral-700'}`}
                                            >
                                                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${cfg.use_telegram_proxy !== 'false' ? 'left-7' : 'left-1'}`} />
                                            </button>
                                        </div>
                                        
                                        <div className={`space-y-4 transition-all duration-300 ${cfg.use_telegram_proxy === 'false' ? 'opacity-40 grayscale pointer-events-none' : 'opacity-100'}`}>
                                            <div className="space-y-3">
                                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">URL Воркера</label>
                                                <input
                                                    type="text"
                                                    value={cfg.telegram_proxy_url || ''}
                                                    onChange={(e) => setCfg({ ...cfg, telegram_proxy_url: e.target.value })}
                                                    className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-mono text-xs"
                                                    placeholder="https://erpmcc.bunyod-sbi.workers.dev"
                                                />
                                                <p className="text-[9px] text-neutral-500 font-bold uppercase tracking-widest leading-relaxed">
                                                    Используйте прокси, если сервер находится в регионе с ограничениями доступа к Telegram API.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] space-y-6">
                                        <h4 className="text-sm font-black text-white uppercase tracking-widest mb-4">Настройки счетов</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-3">
                                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Интервал напоминаний (дни)</label>
                                                <input
                                                    type="number"
                                                    value={cfg.invoice_reminder_interval || '3'}
                                                    onChange={(e) => setCfg({ ...cfg, invoice_reminder_interval: e.target.value })}
                                                    className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-mono text-center font-bold"
                                                    min="1"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            {[
                                                { id: 'notify_invoice_new', label: 'Уведомление о новом счете' },
                                                { id: 'notify_invoice_reminder', label: 'Напоминания об оплате' },
                                                { id: 'notify_invoice_paid', label: 'Подтверждение оплаты' },
                                            ].map(item => (
                                                <div key={item.id} className="flex items-center justify-between p-4 bg-neutral-900/50 rounded-2xl border border-neutral-800">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-neutral-400">{item.label}</span>
                                                    <button
                                                        onClick={() => setCfg({ ...cfg, [item.id]: cfg[item.id] === 'false' ? 'true' : 'false' })}
                                                        className={`w-10 h-5 rounded-full relative transition-colors ${cfg[item.id] !== 'false' ? 'bg-blue-600' : 'bg-neutral-700'}`}
                                                    >
                                                        <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${cfg[item.id] !== 'false' ? 'left-6' : 'left-1'}`} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="lg:col-span-1 space-y-8">
                                    <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] space-y-6">
                                        <h4 className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-2">
                                            <span className="p-2 bg-blue-600/10 rounded-xl">
                                                <Bot className="w-4 h-4 text-blue-500" />
                                            </span>
                                            Уведомления о новых билетах
                                        </h4>
                                        <p className="text-[9px] text-neutral-500 font-bold uppercase tracking-widest leading-relaxed">
                                            Выберите бота и чат (группу/канал) для отправки копии билетов при их покупке или выпуске.
                                        </p>
                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Выберите бота</label>
                                                <select
                                                    value={cfg.ticket_notify_bot_id || ''}
                                                    onChange={(e) => handleBotChange(e.target.value)}
                                                    className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold uppercase text-[10px] tracking-wider"
                                                >
                                                    <option value="">Не отправлять (выключено)</option>
                                                    {bots.map(b => (
                                                        <option key={b.id} value={b.id}>{b.name} (@{b.username || b.id})</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {cfg.ticket_notify_bot_id && (
                                                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                                                    <div className="space-y-2">
                                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Выберите чат для уведомлений</label>
                                                        <select
                                                            value={cfg.ticket_notify_chat_id || ''}
                                                            onChange={(e) => handleChatChange(e.target.value)}
                                                            className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold uppercase text-[10px] tracking-wider"
                                                        >
                                                            <option value="">-- Выберите подключенный чат --</option>
                                                            {chats.map(c => (
                                                                <option key={c.id} value={c.chat_id}>{c.title || c.chat_id} ({c.chat_type})</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    
                                                    <div className="space-y-2">
                                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Или укажите ID чата вручную</label>
                                                        <input
                                                            type="text"
                                                            value={cfg.ticket_notify_chat_id || ''}
                                                            onChange={(e) => handleChatChange(e.target.value)}
                                                            placeholder="-100..."
                                                            className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-4 rounded-2xl outline-none focus:border-blue-500 transition-all font-mono text-sm"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] flex flex-col">
                                        <div className="space-y-3 flex-1">
                                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Приветствие / Описание</label>
                                            <textarea
                                                value={cfg.welcome_msg || ''}
                                                onChange={(e) => setCfg({ ...cfg, welcome_msg: e.target.value })}
                                                className="w-full bg-neutral-900 border-2 border-neutral-800 text-white p-6 rounded-[2rem] outline-none focus:border-blue-500 transition-all h-full min-h-[200px] resize-none font-medium leading-relaxed"
                                                placeholder="Введите текст приветствия..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button onClick={handleSaveCfg} className="w-full md:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-10 py-5 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-600/20 active:scale-95">
                                <Save className="w-4 h-4" />
                                Сохранить настройки бота
                            </button>
                        </div>
                    )}

                    {activeTab === 'ai' && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-neutral-800 pb-8">
                                <div>
                                    <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Интеллект (AI)</h3>
                                    <p className="text-neutral-500 text-sm font-medium">Управление ИИ провайдерами, каскадным переключением и промптами.</p>
                                </div>
                                <div className="flex gap-2">
                                    <button 
                                        onClick={() => { setEditingAI(null); setAiForm({ name: '', provider_type: 'openai', api_key: '', api_base: '', model_name: '', is_active: true }); setIsAIModalOpen(true); }}
                                        className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-600/20 active:scale-95 flex items-center justify-center gap-2"
                                    >
                                        <Plus className="w-4 h-4" /> Добавить провайдера
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="lg:col-span-2 space-y-8">
                                    <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] space-y-6">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-sm font-black text-white uppercase tracking-widest">Провайдеры и Приоритеты</h4>
                                            <div className="flex items-center gap-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest">Использовать ИИ</span>
                                                    <button
                                                        onClick={() => setCfg({ ...cfg, ai_enabled: cfg.ai_enabled === 'true' ? 'false' : 'true' })}
                                                        className={`w-10 h-5 rounded-full relative transition-colors ${cfg.ai_enabled === 'true' ? 'bg-blue-600' : 'bg-neutral-700'}`}
                                                    >
                                                        <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${cfg.ai_enabled === 'true' ? 'left-6' : 'left-1'}`} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <p className="text-xs text-neutral-500 font-medium leading-relaxed">
                                            Перетаскивайте карточки для изменения приоритета. Если первый провайдер не ответит, система автоматически обратится к следующему (Cascade Fallback).
                                        </p>

                                        <DndContext 
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext 
                                                items={aiProviders?.map(p => p.id) || []}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                <div className="space-y-1">
                                                    {aiProviders?.map(provider => (
                                                        <SortableProvider 
                                                            key={provider.id} 
                                                            provider={provider} 
                                                            onEdit={(p) => { setEditingAI(p); setAiForm(p); setIsAIModalOpen(true); }}
                                                            onDelete={(id) => deleteAIProviderMutation.mutate(id)}
                                                        />
                                                    ))}
                                                    {(!aiProviders || aiProviders.length === 0) && (
                                                        <div className="py-12 border-2 border-dashed border-neutral-800 rounded-[2rem] flex flex-col items-center justify-center text-neutral-600">
                                                            <Cpu className="w-12 h-12 mb-4 opacity-10" />
                                                            <p className="text-[10px] font-black uppercase tracking-widest">Нет настроенных провайдеров</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </SortableContext>
                                        </DndContext>
                                    </div>

                                    <div className="bg-neutral-800/30 border border-neutral-800 p-8 rounded-[2.5rem] space-y-6">
                                        <h4 className="text-sm font-black text-white uppercase tracking-widest">Шаблоны промптов</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {aiPrompts?.map(prompt => (
                                                <div key={prompt.id} className="p-4 bg-neutral-900 border border-neutral-800 rounded-2xl flex flex-col justify-between group hover:border-blue-500/30 transition-all">
                                                    <div>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span className="text-xs font-black text-white uppercase tracking-tight">{prompt.name}</span>
                                                            {prompt.is_system && <span className="text-[8px] bg-neutral-800 text-neutral-500 px-1.5 py-0.5 rounded uppercase font-bold">System</span>}
                                                        </div>
                                                        <p className="text-[10px] text-neutral-500 font-medium line-clamp-2">{prompt.description}</p>
                                                    </div>
                                                    <button 
                                                        onClick={() => { setEditingPrompt(prompt); setPromptForm(prompt); setIsPromptModalOpen(true); }}
                                                        className="mt-4 flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest text-blue-500 hover:text-blue-400 transition-colors py-2 border-t border-neutral-800/50"
                                                    >
                                                        <Edit2 size={12} /> Настроить
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div className="bg-blue-600/10 border border-blue-600/20 p-8 rounded-[2.5rem] space-y-4">
                                        <div className="w-12 h-12 bg-blue-600/20 rounded-2xl flex items-center justify-center text-blue-500">
                                            <AlertCircle size={24} />
                                        </div>
                                        <h4 className="text-sm font-black text-white uppercase tracking-widest">О работе каскада</h4>
                                        <p className="text-[11px] text-neutral-400 leading-relaxed font-medium">
                                            Система всегда начинает с провайдера с наивысшим приоритетом (вверху списка). 
                                            При возникновении ошибки 429 (лимиты), 500 или таймаута, запрос будет повторен со следующим провайдером.
                                        </p>
                                        <label className="flex items-center justify-between p-4 bg-blue-600/5 rounded-2xl cursor-pointer hover:bg-blue-600/10 transition-all">
                                            <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Cascade Fallback</span>
                                            <button
                                                onClick={() => setCfg({ ...cfg, ai_cascade_fallback: cfg.ai_cascade_fallback === 'true' ? 'false' : 'true' })}
                                                className={`w-10 h-5 rounded-full relative transition-colors ${cfg.ai_cascade_fallback === 'true' ? 'bg-blue-600' : 'bg-neutral-800'}`}
                                            >
                                                <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${cfg.ai_cascade_fallback === 'true' ? 'left-6' : 'left-1'}`} />
                                            </button>
                                        </label>
                                    </div>
                                    
                                    <button onClick={handleSaveCfg} className="w-full flex items-center justify-center gap-2 bg-neutral-800 hover:bg-neutral-700 text-white px-8 py-5 rounded-[2rem] font-black uppercase text-[10px] tracking-widest transition-all">
                                        <Save className="w-4 h-4" /> Сохранить настройки
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'users' && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-neutral-800 pb-8">
                                <div>
                                    <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Команда</h3>
                                    <p className="text-neutral-500 text-sm font-medium">Управление ролями и доступом сотрудников и партнеров.</p>
                                </div>
                                <button
                                    onClick={() => { setEditingUser(null); setUserForm({ name: '', fio: '', role: 'employee', password: '', profit_percentage: '', timezone: 'Europe/Moscow', can_access_site: true, can_view_company_balance: false, can_view_month_balance: false }); setIsUserModalOpen(true); }}
                                    className="w-full md:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-600/20 active:scale-95"
                                >
                                    <UserPlus className="w-5 h-5" />
                                    Пригласить участника
                                </button>
                            </div>

                            {(() => {
                                const shareholders = appUsers?.filter(u => u.profit_percentage > 0) || [];
                                const totalShare = shareholders.reduce((sum, u) => sum + (u.profit_percentage || 0), 0);
                                const isShareValid = Math.abs(totalShare - 100) < 0.01;

                                if (shareholders.length > 0 && !isShareValid) {
                                    return (
                                        <div className="bg-amber-500/5 border border-amber-500/20 p-6 rounded-[2rem] flex items-center gap-4 animate-pulse">
                                            <div className="w-12 h-12 bg-amber-500/10 rounded-2xl flex items-center justify-center text-amber-500 shrink-0">
                                                <Shield className="w-6 h-6" />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-black uppercase tracking-widest text-amber-500 mb-0.5">Внимание: Дисбаланс долей</p>
                                                <p className="text-xs text-neutral-400 font-medium">Сумма долей составляет <b className="text-amber-500">{totalShare}%</b>. Для корректных расчетов она должна быть ровно 100%.</p>
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })()}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {appUsers?.map(_user => (
                                    <div key={_user.id} className="bg-neutral-800/30 border border-neutral-800 p-6 rounded-[2.5rem] flex items-center justify-between group hover:border-blue-500/30 transition-all duration-300">
                                        <div className="flex items-center gap-4">
                                            <div className="relative">
                                                {_user.photo_url ? (
                                                    <img
                                                        src={_user.photo_url.startsWith('/') ? (_user.photo_url.startsWith('/api') ? _user.photo_url : `/api${_user.photo_url}`) : _user.photo_url}
                                                        alt="User"
                                                        className="w-14 h-14 rounded-2xl object-cover border-2 border-neutral-800 group-hover:border-blue-500/50 transition-colors"
                                                    />
                                                ) : (
                                                    <div className="w-14 h-14 bg-neutral-800 rounded-2xl flex items-center justify-center font-black text-neutral-500 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner">
                                                        {_user.name[0].toUpperCase()}
                                                    </div>
                                                )}
                                                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-neutral-900 ${_user.can_access_site ? 'bg-emerald-500' : 'bg-neutral-600'}`} />
                                            </div>
                                            <div>
                                                <p className="font-black text-white tracking-tight uppercase italic text-sm truncate max-w-[150px] md:max-w-[200px]" title={_user.fio || _user.name}>{_user.fio || _user.name}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-full ${_user.role === 'superadmin' ? 'bg-fuchsia-500/10 text-fuchsia-500' :
                                                        _user.role === 'admin' ? 'bg-amber-500/10 text-amber-500' :
                                                            _user.role === 'partner' ? 'bg-emerald-500/10 text-emerald-500' :
                                                                'bg-blue-500/10 text-blue-500'
                                                        }`}>
                                                        {_user.role}
                                                    </span>
                                                    {_user.profit_percentage > 0 && (
                                                        <span className="text-[8px] font-black text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                                                            {_user.profit_percentage}%
                                                        </span>
                                                    )}
                                                    {_user.telegram_id && (
                                                        <div className="flex items-center gap-1 text-[8px] font-bold text-neutral-500">
                                                            <Bot className="w-2.5 h-2.5" />
                                                            {_user.telegram_id}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all scale-95 group-hover:scale-100">
                                            <button
                                                onClick={() => {
                                                    setEditingUser(_user);
                                                    setUserForm({
                                                        ..._user, password: '', telegram_id: _user.telegram_id || '', timezone: _user.timezone || 'Europe/Moscow',
                                                        can_access_site: _user.can_access_site, can_view_company_balance: _user.can_view_company_balance, can_view_month_balance: _user.can_view_month_balance, can_view_finance: _user.can_view_finance
                                                    });
                                                    setIsUserModalOpen(true);
                                                }}
                                                className="w-10 h-10 bg-neutral-900 rounded-xl flex items-center justify-center text-neutral-500 hover:text-blue-500 hover:bg-neutral-800 transition-all active:scale-95 border border-neutral-800"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => setConfirmModal({
                                                    isOpen: true,
                                                    message: `Удалить пользователя ${_user.name}?`,
                                                    onConfirm: () => {
                                                        deleteUserMutation.mutate(_user.id);
                                                        setConfirmModal({ isOpen: false, message: '', onConfirm: null });
                                                    }
                                                })}
                                                className="w-10 h-10 bg-neutral-900 rounded-xl flex items-center justify-center text-neutral-500 hover:text-rose-500 hover:bg-neutral-800 transition-all active:scale-95 border border-neutral-800"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'categories' && (
                        <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="border-b border-neutral-800 pb-8">
                                <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Категории</h3>
                                <p className="text-neutral-500 text-sm font-medium">Группировка ваших доходов и расходов для аналитики.</p>
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-10">
                                {['income', 'expense'].map(type => (
                                    <div key={type} className="space-y-6">
                                        <div className="flex items-center gap-3 ml-2">
                                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${type === 'income' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>
                                                {type === 'income' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                            </div>
                                            <h4 className="text-[10px] font-black text-neutral-400 uppercase tracking-[0.2em]">{type === 'income' ? 'Доходы' : 'Расходы'}</h4>
                                        </div>

                                        <div className="bg-neutral-800/30 border border-neutral-800 rounded-[2.5rem] p-6 space-y-3 min-h-[400px] flex flex-col shadow-inner">
                                            <div className="flex-1 space-y-2">
                                                {categories?.filter(c => c.type === type).map(cat => (
                                                    <div key={cat.id} className="flex items-center justify-between p-4 bg-neutral-900 border border-neutral-800 rounded-2xl group hover:border-blue-500/30 transition-all duration-300">
                                                        <span className="text-xs font-black text-neutral-300 uppercase tracking-tight">{cat.name}</span>
                                                        <button onClick={() => setConfirmModal({
                                                            isOpen: true,
                                                            message: `Удалить категорию ${cat.name}?`,
                                                            onConfirm: () => {
                                                                deleteCategoryMutation.mutate(cat.id);
                                                                setConfirmModal({ isOpen: false, message: '', onConfirm: null });
                                                            }
                                                        })} className="w-8 h-8 flex items-center justify-center rounded-lg text-neutral-600 opacity-0 group-hover:opacity-100 hover:text-rose-500 hover:bg-rose-500/10 transition-all active:scale-90">
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                ))}
                                                {categories?.filter(c => c.type === type).length === 0 && (
                                                    <div className="h-full flex flex-col items-center justify-center py-10 opacity-20">
                                                        <Inbox className="w-12 h-12 mb-2" />
                                                        <p className="text-[10px] font-black uppercase tracking-widest">Пусто</p>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="pt-6 border-t border-neutral-800/50">
                                                <div className="flex gap-3">
                                                    <input
                                                        type="text"
                                                        placeholder="Новая категория..."
                                                        className="flex-1 bg-neutral-900 border-2 border-neutral-800 text-sm p-4 rounded-2xl outline-none focus:border-blue-500 text-white font-bold transition-all"
                                                        value={newCat.type === type ? newCat.name : ''}
                                                        onChange={(e) => setNewCat({ name: e.target.value, type })}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && newCat.name) {
                                                                addCategoryMutation.mutate(newCat);
                                                                setNewCat({ name: '', type });
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        onClick={() => {
                                                            if (!newCat.name) return;
                                                            addCategoryMutation.mutate(newCat);
                                                            setNewCat({ name: '', type });
                                                        }}
                                                        className="w-14 h-14 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl flex items-center justify-center transition-all shadow-lg shadow-blue-600/20 active:scale-90"
                                                    >
                                                        <Plus className="w-6 h-6" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    )}



                </div>
            </div>

            {/* User Modal */}
            {
                isUserModalOpen && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
                        <div className="bg-neutral-900 border border-neutral-800 w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90dvh] flex flex-col">
                            <div className="p-8 md:p-12 overflow-y-auto custom-scrollbar flex-1">
                                <div className="flex items-center justify-between mb-10 sticky top-0 bg-neutral-900/90 backdrop-blur-md pb-4 z-10">
                                    <h3 className="text-3xl font-black text-white uppercase tracking-tighter italic">
                                        {editingUser ? 'Профиль участника' : 'Новый участник'}
                                    </h3>
                                    <button onClick={() => setIsUserModalOpen(false)} className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center text-neutral-400 hover:text-white transition-colors">
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                <form onSubmit={(e) => {
                                    e.preventDefault();
                                    if (editingUser) updateUserMutation.mutate({ id: editingUser.id, data: userForm });
                                    else createUserMutation.mutate(userForm);
                                }} className="space-y-8">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Логин</label>
                                            <input
                                                type="text"
                                                required
                                                value={userForm.name}
                                                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                                                className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold placeholder:text-neutral-700"
                                                placeholder="ivan_ov"
                                            />
                                        </div>
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Полное имя (ФИО)</label>
                                            <input
                                                type="text"
                                                value={userForm.fio || ''}
                                                onChange={(e) => setUserForm({ ...userForm, fio: e.target.value })}
                                                className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold placeholder:text-neutral-700"
                                                placeholder="Иванов Иван Иванович"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Роль в системе</label>
                                            <select
                                                value={userForm.role}
                                                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                                                className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold appearance-none"
                                            >
                                                <option value="employee">Сотрудник</option>
                                                <option value="partner">Партнер</option>
                                                <option value="admin">Администратор</option>
                                                {user?.role === 'superadmin' && <option value="superadmin">Супер-админ</option>}
                                            </select>
                                        </div>
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Доля прибыли (%)</label>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    value={userForm.profit_percentage || ''}
                                                    onChange={(e) => setUserForm({ ...userForm, profit_percentage: e.target.value })}
                                                    className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold placeholder:text-neutral-700"
                                                    placeholder="0"
                                                />
                                                <span className="absolute right-5 top-1/2 -translate-y-1/2 font-black text-neutral-600">%</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">{editingUser ? 'Обновить пароль (опционально)' : 'Пароль доступа'}</label>
                                        <input
                                            type="password"
                                            required={!editingUser}
                                            value={userForm.password}
                                            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                                            className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold"
                                            placeholder="••••••••"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Часовой пояс</label>
                                            <select
                                                value={userForm.timezone || 'Europe/Moscow'}
                                                onChange={(e) => setUserForm({ ...userForm, timezone: e.target.value })}
                                                className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold appearance-none"
                                            >
                                                <option value="Europe/Moscow">Москва (UTC+3)</option>
                                                <option value="Asia/Tashkent">Ташкент (UTC+5)</option>
                                                <option value="Asia/Dubai">Дубай (UTC+4)</option>
                                                <option value="Asia/Almaty">Алматы (UTC+5)</option>
                                                <option value="UTC">UTC</option>
                                            </select>
                                        </div>
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Telegram ID</label>
                                            <input
                                                type="number"
                                                value={userForm.telegram_id || ''}
                                                onChange={(e) => setUserForm({ ...userForm, telegram_id: e.target.value })}
                                                className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold placeholder:text-neutral-700"
                                                placeholder="123456789"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-6 pt-6 border-t border-neutral-800">
                                        <h4 className="text-[10px] font-black text-neutral-500 uppercase tracking-[0.2em] ml-1">Привилегии и доступ</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {[
                                                { key: 'can_access_site', label: 'Доступ к ERP', desc: 'Вход в веб-интерфейс' },
                                                { key: 'can_view_finance', label: 'Все финансы', desc: 'Полный доступ к данным' },
                                                { key: 'can_view_company_balance', label: 'Баланс компании', desc: 'Сумма всех кошельков' },
                                                { key: 'can_view_month_balance', label: 'Отчеты месяца', desc: 'Доступ к статистике' }
                                            ].map(item => (
                                                <label key={item.key} className="flex items-center justify-between p-4 bg-neutral-800/30 border border-neutral-800 rounded-2xl cursor-pointer hover:border-blue-500/50 transition-all group">
                                                    <div>
                                                        <span className="text-xs font-black text-white block uppercase tracking-tight">{item.label}</span>
                                                        <span className="text-[9px] text-neutral-500 font-bold uppercase tracking-tighter">{item.desc}</span>
                                                    </div>
                                                    <div className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only peer"
                                                            checked={userForm[item.key]}
                                                            onChange={(e) => setUserForm({ ...userForm, [item.key]: e.target.checked })}
                                                        />
                                                        <div className="w-10 h-5 bg-neutral-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex gap-4 pt-10">
                                        <button
                                            type="button"
                                            onClick={() => setIsUserModalOpen(false)}
                                            className="flex-1 py-5 bg-neutral-800 hover:bg-neutral-700 text-neutral-400 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all active:scale-95"
                                        >
                                            Отмена
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={updateUserMutation.isLoading || createUserMutation.isLoading}
                                            className="flex-1 py-5 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-600/20 active:scale-95 disabled:opacity-50"
                                        >
                                            {updateUserMutation.isLoading || createUserMutation.isLoading ? 'Сохранение...' : 'Подтвердить'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Wallet Modal */}
            {
                isWalletModalOpen && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
                        <div className="bg-neutral-900 border border-neutral-800 w-full max-w-lg rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90dvh] flex flex-col">
                            <div className="p-8 md:p-12 overflow-y-auto custom-scrollbar flex-1">
                                <div className="flex items-center justify-between mb-10 sticky top-0 bg-neutral-900/90 backdrop-blur-md pb-4 z-10">
                                    <h3 className="text-3xl font-black text-white uppercase tracking-tighter italic">
                                        {editingWallet ? 'Настройка счета' : 'Новый кошелек'}
                                    </h3>
                                    <button onClick={() => setIsWalletModalOpen(false)} className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center text-neutral-400 hover:text-white transition-colors">
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                <form onSubmit={(e) => {
                                    e.preventDefault();
                                    if (editingWallet) updateWalletMutation.mutate({ id: editingWallet.id, data: walletForm });
                                    else createWalletMutation.mutate(walletForm);
                                }} className="space-y-8">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Название счета</label>
                                        <input
                                            type="text"
                                            required
                                            value={walletForm.name}
                                            onChange={(e) => setWalletForm({ ...walletForm, name: e.target.value })}
                                            className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold placeholder:text-neutral-700"
                                            placeholder="Напр: Т-Банк Основной"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Текущий остаток</label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                required
                                                value={walletForm.balance}
                                                onChange={(e) => setWalletForm({ ...walletForm, balance: e.target.value })}
                                                className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-black text-2xl italic tracking-tighter"
                                                placeholder="0.00"
                                            />
                                            <span className="absolute right-6 top-1/2 -translate-y-1/2 font-black text-neutral-600">₽</span>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Владелец счета</label>
                                        <select
                                            value={walletForm.owner_id}
                                            onChange={(e) => setWalletForm({ ...walletForm, owner_id: e.target.value })}
                                            className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold appearance-none"
                                        >
                                            <option value="">Без владельца (Общий)</option>
                                            {appUsers?.map(u => (
                                                <option key={u.id} value={u.id}>{u.fio || u.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex gap-4 pt-10">
                                        <button
                                            type="button"
                                            onClick={() => setIsWalletModalOpen(false)}
                                            className="flex-1 py-5 bg-neutral-800 hover:bg-neutral-700 text-neutral-400 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all active:scale-95"
                                        >
                                            Отмена
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={updateWalletMutation.isLoading || createWalletMutation.isLoading}
                                            className="flex-1 py-5 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-600/20 active:scale-95 disabled:opacity-50"
                                        >
                                            {updateWalletMutation.isLoading || createWalletMutation.isLoading ? 'Сохранение...' : 'Подтвердить'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )
            }



            {/* AI Provider Modal */}
            {isAIModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className="bg-neutral-900 border border-neutral-800 w-full max-w-lg rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90dvh] flex flex-col">
                        <div className="p-8 md:p-12 overflow-y-auto custom-scrollbar flex-1">
                            <div className="flex items-center justify-between mb-10 sticky top-0 bg-neutral-900/90 backdrop-blur-md pb-4 z-10">
                                <h3 className="text-3xl font-black text-white uppercase tracking-tighter italic">
                                    {editingAI ? 'Настройка ИИ' : 'Новое соединение'}
                                </h3>
                                <button onClick={() => setIsAIModalOpen(false)} className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center text-neutral-400 hover:text-white transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <form onSubmit={(e) => {
                                e.preventDefault();
                                upsertAIProviderMutation.mutate(aiForm);
                            }} className="space-y-6">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Название (для себя)</label>
                                    <input
                                        type="text"
                                        required
                                        value={aiForm.name}
                                        onChange={(e) => setAiForm({ ...aiForm, name: e.target.value })}
                                        className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold"
                                        placeholder="Основной OpenAI / Fallback Gemini"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Тип провайдера</label>
                                        <select
                                            value={aiForm.provider_type}
                                            onChange={(e) => setAiForm({ ...aiForm, provider_type: e.target.value })}
                                            className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold appearance-none"
                                        >
                                            <option value="openai">OpenAI</option>
                                            <option value="gemini">Google Gemini</option>
                                            <option value="cloudflare">Cloudflare Workers AI</option>
                                            <option value="openrouter">OpenRouter</option>
                                            <option value="anthropic">Anthropic Claude</option>
                                            <option value="custom">Custom (OpenAI Compatible)</option>
                                        </select>
                                    </div>
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Модель</label>
                                        <input
                                            type="text"
                                            value={aiForm.model_name}
                                            onChange={(e) => setAiForm({ ...aiForm, model_name: e.target.value })}
                                            className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold"
                                            placeholder="gpt-4o / gemini-1.5-pro"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">API Ключ</label>
                                    <input
                                        type="password"
                                        required={!editingAI}
                                        value={aiForm.api_key}
                                        onChange={(e) => setAiForm({ ...aiForm, api_key: e.target.value })}
                                        className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold"
                                        placeholder="sk-..."
                                    />
                                </div>

                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Base URL (опционально)</label>
                                    <input
                                        type="text"
                                        value={aiForm.api_base}
                                        onChange={(e) => setAiForm({ ...aiForm, api_base: e.target.value })}
                                        className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold"
                                        placeholder="https://api.openai.com/v1"
                                    />
                                </div>

                                <label className="flex items-center justify-between p-4 bg-neutral-800/30 border border-neutral-800 rounded-2xl cursor-pointer">
                                    <span className="text-xs font-black text-white uppercase tracking-tight">Активен</span>
                                    <button
                                        type="button"
                                        onClick={() => setAiForm({ ...aiForm, is_active: !aiForm.is_active })}
                                        className={`w-10 h-5 rounded-full relative transition-colors ${aiForm.is_active ? 'bg-blue-600' : 'bg-neutral-700'}`}
                                    >
                                        <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${aiForm.is_active ? 'left-6' : 'left-1'}`} />
                                    </button>
                                </label>

                                <div className="flex gap-4 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setIsAIModalOpen(false)}
                                        className="flex-1 py-4 bg-neutral-800 hover:bg-neutral-700 text-neutral-400 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all"
                                    >
                                        Отмена
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={upsertAIProviderMutation.isLoading}
                                        className="flex-1 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-600/20 active:scale-95 disabled:opacity-50"
                                    >
                                        {upsertAIProviderMutation.isLoading ? 'Сохранение...' : 'Сохранить'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Prompt Modal */}
            {isPromptModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className="bg-neutral-900 border border-neutral-800 w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90dvh] flex flex-col">
                        <div className="p-8 md:p-12 overflow-y-auto custom-scrollbar flex-1">
                            <div className="flex items-center justify-between mb-8 sticky top-0 bg-neutral-900/90 backdrop-blur-md pb-4 z-10">
                                <div>
                                    <h3 className="text-3xl font-black text-white uppercase tracking-tighter italic">
                                        Настройка шаблона
                                    </h3>
                                    <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest mt-1">{promptForm.name}</p>
                                </div>
                                <button onClick={() => setIsPromptModalOpen(false)} className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center text-neutral-400 hover:text-white transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <form onSubmit={(e) => {
                                e.preventDefault();
                                upsertAIPromptMutation.mutate(promptForm);
                            }} className="space-y-6">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Инструкция (System Prompt)</label>
                                    <textarea
                                        required
                                        value={promptForm.template}
                                        onChange={(e) => setPromptForm({ ...promptForm, template: e.target.value })}
                                        className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-6 rounded-[2rem] outline-none focus:border-blue-500 transition-all min-h-[300px] font-medium leading-relaxed"
                                        placeholder="Вы - эксперт по продажам..."
                                    />
                                    <p className="text-[9px] text-neutral-600 font-bold uppercase tracking-widest mt-2 px-2">
                                        Вы можете использовать переменные в формате {"{variable_name}"} для динамической подстановки данных.
                                    </p>
                                </div>

                                <div className="flex gap-4 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setIsPromptModalOpen(false)}
                                        className="flex-1 py-5 bg-neutral-800 hover:bg-neutral-700 text-neutral-400 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all"
                                    >
                                        Отмена
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={upsertAIPromptMutation.isLoading}
                                        className="flex-1 py-5 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-blue-600/20 active:scale-95 disabled:opacity-50"
                                    >
                                        {upsertAIPromptMutation.isLoading ? 'Сохранение...' : 'Сохранить шаблон'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Modal */}
            {
                confirmModal.isOpen && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
                        <div className="bg-neutral-900 border border-neutral-800 rounded-[2.5rem] p-8 md:p-10 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300">
                            <div className="w-16 h-16 bg-rose-500/10 text-rose-500 rounded-2xl flex items-center justify-center mb-6">
                                <AlertTriangle className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-black text-white uppercase tracking-tighter mb-2">Подтвердите удаление</h3>
                            <p className="text-neutral-500 text-sm font-medium mb-8 leading-relaxed">{confirmModal.message}</p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setConfirmModal({ isOpen: false, message: '', onConfirm: null })}
                                    className="flex-1 bg-neutral-800 hover:bg-neutral-700 text-neutral-400 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all active:scale-95"
                                >
                                    Отмена
                                </button>
                                <button
                                    onClick={() => {
                                        confirmModal.onConfirm();
                                        setConfirmModal({ isOpen: false, message: '', onConfirm: null });
                                    }}
                                    className="flex-1 bg-rose-600 hover:bg-rose-500 text-white py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-lg shadow-rose-600/20 active:scale-95"
                                >
                                    Удалить
                                </button>
                            </div>
                        </div>
                    </div>
                )}
        </div >
    );
};

export default Settings;
